import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Award,
    Upload,
    Sparkles,
    Trash2,
    Download,
    FileText,
    Building2,
    Banknote,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Plus,
    Pencil,
    Receipt,
    Briefcase,
    ShieldCheck,
    BookOpenCheck,
    X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    getSolvencyProfile,
    listSolvencyDocuments,
    uploadSolvencyDocument,
    deleteSolvencyDocument,
    listSolvencyRecords,
    createSolvencyRecord,
    updateSolvencyRecord,
    deleteSolvencyRecord,
    solvencyExportPdfUrl,
    solvencyExportXlsxUrl,
} from "@/lib/api";
import { fmtEUR } from "@/lib/format";

// ---- Doc kinds metadata ----
const DOC_KINDS = [
    {
        key: "factura",
        label: "Facturas",
        icon: Receipt,
        cls: "from-blue-500 to-indigo-600",
        accent: "text-blue-700 bg-blue-50 border-blue-200",
        desc: "Facturas emitidas a clientes — alimentan el cuadro de solvencia técnica.",
    },
    {
        key: "certificado",
        label: "Certificados",
        icon: BookOpenCheck,
        cls: "from-emerald-500 to-teal-600",
        accent: "text-emerald-700 bg-emerald-50 border-emerald-200",
        desc: "Certificados de buena ejecución firmados por el cliente.",
    },
    {
        key: "cuenta_anual",
        label: "Cuentas anuales",
        icon: Briefcase,
        cls: "from-violet-500 to-fuchsia-600",
        accent: "text-violet-700 bg-violet-50 border-violet-200",
        desc: "Depósito de cuentas en el Registro Mercantil — alimentan el cuadro económico.",
    },
    {
        key: "poliza",
        label: "Pólizas RC",
        icon: ShieldCheck,
        cls: "from-amber-500 to-orange-600",
        accent: "text-amber-700 bg-amber-50 border-amber-200",
        desc: "Pólizas de Responsabilidad Civil en vigor.",
    },
];

const kindLabel = (k) => DOC_KINDS.find((d) => d.key === k)?.label || k;

const fmtAmount = (v) =>
    v === null || v === undefined || isNaN(Number(v))
        ? "—"
        : fmtEUR(Number(v));

export default function Solvencia() {
    const [profile, setProfile] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("tecnica"); // tecnica | economica | documentos
    const [uploading, setUploading] = useState(null); // kind being uploaded
    const [exportingPdf, setExportingPdf] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null); // record being edited or null
    const [newRecordType, setNewRecordType] = useState(null); // 'tecnica' | 'economica' | null
    const fileInputs = useRef({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, d, r] = await Promise.all([
                getSolvencyProfile(),
                listSolvencyDocuments(),
                listSolvencyRecords(),
            ]);
            setProfile(p);
            setDocuments(d.documents || []);
            setRecords(r.records || []);
        } catch (e) {
            toast.error("No se pudo cargar el perfil de solvencia");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleUpload = async (kind, file) => {
        if (!file) return;
        setUploading(kind);
        try {
            const res = await uploadSolvencyDocument(kind, file);
            if (res.ai_status === "done") {
                toast.success(
                    `IA extrajo ${res.records?.length || 0} registro${(res.records || []).length === 1 ? "" : "s"} de "${file.name}"`
                );
            } else {
                toast.warning(
                    `Documento subido pero la IA no pudo extraer datos${res.ai_error ? `: ${res.ai_error.slice(0, 80)}` : ""}`
                );
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir el documento");
        } finally {
            setUploading(null);
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm("¿Borrar el documento y los registros derivados?")) return;
        try {
            await deleteSolvencyDocument(docId);
            toast.success("Documento eliminado");
            await load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const handleDeleteRecord = async (recId) => {
        if (!window.confirm("¿Borrar este registro?")) return;
        try {
            await deleteSolvencyRecord(recId);
            toast.success("Registro eliminado");
            await load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const handleExportPdf = (section = "all") => {
        setExportingPdf(true);
        // Open in same tab — browser will trigger download from Content-Disposition.
        // We use a fetch + blob so cookies (httpOnly session) are sent.
        const url = solvencyExportPdfUrl(section);
        fetch(url, { credentials: "include" })
            .then((r) => {
                if (!r.ok) throw new Error("HTTP " + r.status);
                return r.blob();
            })
            .then((blob) => {
                const a = document.createElement("a");
                const objUrl = URL.createObjectURL(blob);
                a.href = objUrl;
                a.download = `solvencia_${section === "all" ? "completo" : section}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objUrl);
                toast.success("PDF descargado");
            })
            .catch(() => toast.error("Error al exportar PDF"))
            .finally(() => setExportingPdf(false));
    };

    const handleExportXlsx = () => {
        const url = solvencyExportXlsxUrl();
        fetch(url, { credentials: "include" })
            .then((r) => {
                if (!r.ok) throw new Error("HTTP " + r.status);
                return r.blob();
            })
            .then((blob) => {
                const a = document.createElement("a");
                const objUrl = URL.createObjectURL(blob);
                a.href = objUrl;
                a.download = "solvencia_cuadros.xlsx";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objUrl);
                toast.success("Excel descargado");
            })
            .catch(() => toast.error("Error al exportar Excel"));
    };

    const tecnica = records.filter((r) => r.type === "tecnica");
    const economica = records.filter((r) => r.type === "economica");

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="solvencia-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600 mb-2 flex items-center gap-1.5">
                            <Award size={12} />
                            LCSP Art. 87-90 · Cuadros profesionales
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Solvencia
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Sube facturas, cuentas anuales y pólizas — Claude 4.5 extrae los
                            datos y genera los cuadros oficiales de{" "}
                            <strong className="text-ink-700">solvencia técnica</strong> y{" "}
                            <strong className="text-ink-700">económico-financiera</strong> que
                            puedes adjuntar a cualquier licitación.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={handleExportXlsx}
                            className="rounded-full border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 h-10 px-5 font-bold gap-2"
                            data-testid="solvencia-export-xlsx"
                        >
                            <Download size={14} />
                            Excel
                        </Button>
                        <Button
                            onClick={() => handleExportPdf("all")}
                            disabled={exportingPdf}
                            className="rounded-full bg-ink-950 hover:bg-ink-900 text-white h-10 px-5 font-bold gap-2 shadow-pop"
                            data-testid="solvencia-export-pdf"
                        >
                            {exportingPdf ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Download size={14} />
                            )}
                            PDF completo
                        </Button>
                    </div>
                </div>

                {/* KPI strip */}
                <div className="grid md:grid-cols-4 gap-3 mb-8">
                    {[
                        {
                            label: "Documentos",
                            value: profile?.documents_count ?? 0,
                            sub: "subidos",
                            icon: FileText,
                            color: "from-slate-700 to-slate-900",
                        },
                        {
                            label: "Cuadro técnico",
                            value: profile?.tecnica_count ?? 0,
                            sub: "servicios registrados",
                            icon: BookOpenCheck,
                            color: "from-blue-600 to-indigo-700",
                        },
                        {
                            label: "Cuadro económico",
                            value: profile?.economica_count ?? 0,
                            sub: "registros financieros",
                            icon: Banknote,
                            color: "from-emerald-600 to-teal-700",
                        },
                        {
                            label: "Volumen acreditado",
                            value: fmtAmount(profile?.tecnica_total_eur),
                            sub: "suma técnica acumulada",
                            icon: Award,
                            color: "from-amber-600 to-orange-700",
                            isMoney: true,
                        },
                    ].map((k, i) => {
                        const Icon = k.icon;
                        return (
                            <motion.div
                                key={k.label}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${k.color} text-white shadow-pop`}
                                data-testid={`solvencia-kpi-${i}`}
                            >
                                <div className="absolute -top-3 -right-3 opacity-15">
                                    <Icon size={70} />
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80 mb-1">
                                    {k.label}
                                </div>
                                <div className={`font-display font-black tabular-nums leading-none ${k.isMoney ? "text-2xl" : "text-4xl"}`}>
                                    {k.value}
                                </div>
                                <div className="text-[11px] font-medium opacity-70 mt-1">
                                    {k.sub}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Upload zone — 4 cards */}
                <div className="bg-white border border-ink-100 rounded-2xl p-5 md:p-6 shadow-pop mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles size={14} className="text-violet-600" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500">
                            Subir documentos · La IA extrae los datos
                        </span>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {DOC_KINDS.map((k) => {
                            const Icon = k.icon;
                            const isUp = uploading === k.key;
                            return (
                                <label
                                    key={k.key}
                                    htmlFor={`upload-${k.key}`}
                                    className={`group relative cursor-pointer rounded-2xl border-2 border-dashed border-ink-200 hover:border-brand-400 hover:bg-brand-50/30 p-4 transition-all ${isUp ? "pointer-events-none opacity-70" : ""}`}
                                    data-testid={`solvencia-upload-${k.key}`}
                                >
                                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${k.cls} text-white mb-3 shadow-pop`}>
                                        <Icon size={20} strokeWidth={2.5} />
                                    </div>
                                    <div className="font-display text-base font-black text-ink-950 leading-tight mb-1">
                                        {k.label}
                                    </div>
                                    <div className="text-xs text-ink-500 font-medium leading-snug mb-3">
                                        {k.desc}
                                    </div>
                                    <div className={`inline-flex items-center gap-1.5 text-xs font-bold ${isUp ? "text-amber-600" : "text-brand-600"}`}>
                                        {isUp ? (
                                            <>
                                                <Loader2 size={12} className="animate-spin" />
                                                Analizando con IA…
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={12} />
                                                Subir PDF / imagen
                                            </>
                                        )}
                                    </div>
                                    <input
                                        ref={(el) => (fileInputs.current[k.key] = el)}
                                        id={`upload-${k.key}`}
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                                        disabled={Boolean(uploading)}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleUpload(k.key, file);
                                            e.target.value = "";
                                        }}
                                    />
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-2 mb-5 border-b border-ink-100">
                    {[
                        { key: "tecnica", label: "Solvencia técnica", count: tecnica.length, icon: BookOpenCheck },
                        { key: "economica", label: "Solvencia económica", count: economica.length, icon: Banknote },
                        { key: "documentos", label: "Documentos", count: documents.length, icon: FileText },
                    ].map((t) => {
                        const Icon = t.icon;
                        const active = activeTab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setActiveTab(t.key)}
                                className={`relative px-4 py-3 inline-flex items-center gap-2 text-sm font-bold transition-colors ${active ? "text-ink-950" : "text-ink-400 hover:text-ink-700"}`}
                                data-testid={`solvencia-tab-${t.key}`}
                            >
                                <Icon size={14} />
                                {t.label}
                                <span
                                    className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${active ? "bg-ink-950 text-white" : "bg-ink-50 text-ink-500"}`}
                                >
                                    {t.count}
                                </span>
                                {active && (
                                    <motion.div
                                        layoutId="solvencia-tab-underline"
                                        className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink-950"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Tab content */}
                {loading ? (
                    <div className="bg-white border border-ink-100 rounded-2xl p-12 text-center">
                        <Loader2 className="mx-auto text-brand-600 animate-spin mb-3" size={28} />
                        <div className="text-sm font-bold text-ink-500">Cargando perfil…</div>
                    </div>
                ) : activeTab === "tecnica" ? (
                    <TecnicaTab
                        records={tecnica}
                        onDelete={handleDeleteRecord}
                        onEdit={setEditingRecord}
                        onCreate={() => setNewRecordType("tecnica")}
                        onExport={() => handleExportPdf("tecnica")}
                    />
                ) : activeTab === "economica" ? (
                    <EconomicaTab
                        records={economica}
                        onDelete={handleDeleteRecord}
                        onEdit={setEditingRecord}
                        onCreate={() => setNewRecordType("economica")}
                        onExport={() => handleExportPdf("economica")}
                    />
                ) : (
                    <DocumentosTab
                        documents={documents}
                        onDelete={handleDeleteDocument}
                    />
                )}

                {/* Edit / Create record modal */}
                <AnimatePresence>
                    {(editingRecord || newRecordType) && (
                        <RecordEditor
                            record={editingRecord}
                            initialType={newRecordType}
                            onClose={() => {
                                setEditingRecord(null);
                                setNewRecordType(null);
                            }}
                            onSaved={async () => {
                                setEditingRecord(null);
                                setNewRecordType(null);
                                await load();
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
}

// ---- TecnicaTab ----
function TecnicaTab({ records, onDelete, onEdit, onCreate, onExport }) {
    const sorted = [...records].sort(
        (a, b) => (b.data?.fecha || "").localeCompare(a.data?.fecha || "")
    );
    return (
        <div className="bg-white border border-ink-100 rounded-2xl shadow-pop">
            <div className="flex items-center justify-between p-5 border-b border-ink-100">
                <div>
                    <div className="font-display text-xl font-black text-ink-950">
                        Cuadro 1 · Solvencia técnica
                    </div>
                    <div className="text-xs text-ink-500 font-medium mt-0.5">
                        LCSP Art. 88-90 · Relación de los principales servicios prestados en los últimos 3-5 años
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={onCreate}
                        className="rounded-full h-9 px-4 border-ink-200 font-bold gap-1.5"
                        data-testid="solvencia-tecnica-add"
                    >
                        <Plus size={13} />
                        Añadir manual
                    </Button>
                    <Button
                        onClick={onExport}
                        className="rounded-full bg-ink-950 hover:bg-ink-900 text-white h-9 px-4 font-bold gap-1.5"
                        data-testid="solvencia-tecnica-export"
                    >
                        <Download size={13} />
                        Exportar este cuadro
                    </Button>
                </div>
            </div>
            {sorted.length === 0 ? (
                <div className="p-12 text-center">
                    <BookOpenCheck className="mx-auto text-ink-300 mb-3" size={36} />
                    <div className="font-display text-xl font-black mb-1">Sin registros</div>
                    <div className="text-sm text-ink-500 font-medium">
                        Sube facturas o certificados, o añade manualmente.
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-ink-50 border-b border-ink-100">
                            <tr className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                <th className="text-left px-4 py-3 w-10">Nº</th>
                                <th className="text-left px-4 py-3">Cliente</th>
                                <th className="text-left px-4 py-3 w-20">Tipo</th>
                                <th className="text-left px-4 py-3">Objeto</th>
                                <th className="text-left px-4 py-3 w-24">Naturaleza</th>
                                <th className="text-left px-4 py-3 w-28">Fecha</th>
                                <th className="text-right px-4 py-3 w-32">Importe</th>
                                <th className="text-center px-4 py-3 w-20">Cert.</th>
                                <th className="px-4 py-3 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r, idx) => {
                                const d = r.data || {};
                                return (
                                    <tr
                                        key={r.id}
                                        className="border-b border-ink-50 hover:bg-ink-50/50 transition-colors"
                                        data-testid={`solvencia-tecnica-row-${r.id}`}
                                    >
                                        <td className="px-4 py-3 font-mono text-ink-400 text-xs tabular-nums">
                                            {idx + 1}
                                        </td>
                                        <td className="px-4 py-3 font-bold text-ink-950">
                                            {d.cliente || "—"}
                                            {d.cliente_cif && (
                                                <span className="block text-[10px] font-mono text-ink-400 mt-0.5">
                                                    {d.cliente_cif}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${d.cliente_tipo === "publico" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}
                                            >
                                                {d.cliente_tipo === "publico" ? "Pública" : "Privada"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-ink-700">
                                            {d.objeto || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-ink-500 font-medium capitalize">
                                            {d.naturaleza || "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-ink-500 text-xs">
                                            {d.fecha || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono font-bold text-ink-950 tabular-nums">
                                            {fmtAmount(d.importe)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {d.certificado ? (
                                                <CheckCircle2 className="text-emerald-600 inline" size={16} />
                                            ) : (
                                                <span className="text-ink-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex gap-1 justify-end">
                                                <button
                                                    onClick={() => onEdit(r)}
                                                    className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600 transition-colors"
                                                    title="Editar"
                                                    data-testid={`solvencia-edit-${r.id}`}
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    onClick={() => onDelete(r.id)}
                                                    className="p-1.5 rounded-md hover:bg-red-50 text-red-500 transition-colors"
                                                    title="Eliminar"
                                                    data-testid={`solvencia-del-${r.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---- EconomicaTab ----
function EconomicaTab({ records, onDelete, onEdit, onCreate, onExport }) {
    const volumen = records
        .filter((r) => r.subtype === "volumen")
        .sort((a, b) => (b.year || 0) - (a.year || 0));
    const seguros = records.filter((r) => r.subtype === "seguro");
    return (
        <div className="space-y-6">
            <div className="bg-white border border-ink-100 rounded-2xl shadow-pop">
                <div className="flex items-center justify-between p-5 border-b border-ink-100">
                    <div>
                        <div className="font-display text-xl font-black text-ink-950">
                            Cuadro 2.1 · Volumen anual de negocios
                        </div>
                        <div className="text-xs text-ink-500 font-medium mt-0.5">
                            LCSP Art. 87.1 · últimos 3 ejercicios
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={onCreate}
                            className="rounded-full h-9 px-4 border-ink-200 font-bold gap-1.5"
                            data-testid="solvencia-eco-add"
                        >
                            <Plus size={13} />
                            Añadir ejercicio
                        </Button>
                        <Button
                            onClick={onExport}
                            className="rounded-full bg-ink-950 hover:bg-ink-900 text-white h-9 px-4 font-bold gap-1.5"
                            data-testid="solvencia-eco-export"
                        >
                            <Download size={13} />
                            Exportar este cuadro
                        </Button>
                    </div>
                </div>
                {volumen.length === 0 ? (
                    <div className="p-12 text-center">
                        <Banknote className="mx-auto text-ink-300 mb-3" size={36} />
                        <div className="font-display text-xl font-black mb-1">
                            Sin ejercicios registrados
                        </div>
                        <div className="text-sm text-ink-500 font-medium">
                            Sube las Cuentas Anuales depositadas en el Registro Mercantil.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-ink-50 border-b border-ink-100">
                                <tr className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                    <th className="text-left px-4 py-3 w-20">Ejercicio</th>
                                    <th className="text-right px-4 py-3">Cifra de negocios</th>
                                    <th className="text-right px-4 py-3">Activo corriente</th>
                                    <th className="text-right px-4 py-3">Pasivo corriente</th>
                                    <th className="text-right px-4 py-3 w-24">Ratio</th>
                                    <th className="text-right px-4 py-3">Patrimonio neto</th>
                                    <th className="text-right px-4 py-3">Resultado</th>
                                    <th className="px-4 py-3 w-24"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {volumen.map((r) => {
                                    const d = r.data || {};
                                    const ratio =
                                        d.ratio_solvencia ??
                                        (d.activo_corriente && d.pasivo_corriente
                                            ? d.activo_corriente / d.pasivo_corriente
                                            : null);
                                    return (
                                        <tr key={r.id} className="border-b border-ink-50 hover:bg-ink-50/50">
                                            <td className="px-4 py-3 font-display text-xl font-black text-ink-950">
                                                {r.year || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">
                                                {fmtAmount(d.cifra_negocios)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-ink-700 tabular-nums">
                                                {fmtAmount(d.activo_corriente)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-ink-700 tabular-nums">
                                                {fmtAmount(d.pasivo_corriente)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono tabular-nums">
                                                {ratio !== null && ratio !== undefined ? (
                                                    <span
                                                        className={`px-2 py-0.5 rounded-md font-bold text-xs ${ratio >= 1 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
                                                    >
                                                        {Number(ratio).toFixed(2)}
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-ink-700 tabular-nums">
                                                {fmtAmount(d.patrimonio_neto)}
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono text-ink-700 tabular-nums">
                                                {fmtAmount(d.resultado_ejercicio)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <button
                                                        onClick={() => onEdit(r)}
                                                        className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600"
                                                        data-testid={`solvencia-edit-${r.id}`}
                                                    >
                                                        <Pencil size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => onDelete(r.id)}
                                                        className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
                                                        data-testid={`solvencia-del-${r.id}`}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Seguros */}
            <div className="bg-white border border-ink-100 rounded-2xl shadow-pop">
                <div className="flex items-center justify-between p-5 border-b border-ink-100">
                    <div>
                        <div className="font-display text-xl font-black text-ink-950">
                            Cuadro 2.2 · Seguros de responsabilidad civil
                        </div>
                        <div className="text-xs text-ink-500 font-medium mt-0.5">
                            LCSP Art. 87.2 · pólizas vigentes
                        </div>
                    </div>
                </div>
                {seguros.length === 0 ? (
                    <div className="p-10 text-center">
                        <ShieldCheck className="mx-auto text-ink-300 mb-3" size={32} />
                        <div className="text-sm text-ink-500 font-medium">
                            Sin pólizas registradas — sube tu RC General o RC Profesional.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-ink-50 border-b border-ink-100">
                                <tr className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                    <th className="text-left px-4 py-3">Aseguradora</th>
                                    <th className="text-left px-4 py-3 w-32">Nº Póliza</th>
                                    <th className="text-left px-4 py-3 w-32">Tipo</th>
                                    <th className="text-right px-4 py-3">Suma asegurada</th>
                                    <th className="text-left px-4 py-3 w-44">Vigencia</th>
                                    <th className="px-4 py-3 w-24"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {seguros.map((r) => {
                                    const d = r.data || {};
                                    return (
                                        <tr key={r.id} className="border-b border-ink-50 hover:bg-ink-50/50">
                                            <td className="px-4 py-3 font-bold text-ink-950">
                                                {d.aseguradora || "—"}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-ink-500">
                                                {d.numero_poliza || "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                                    {d.tipo || "RC"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">
                                                {fmtAmount(d.suma_asegurada)}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-ink-500">
                                                {d.vigencia_inicio || "—"} → {d.vigencia_fin || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <button
                                                        onClick={() => onEdit(r)}
                                                        className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600"
                                                        data-testid={`solvencia-edit-${r.id}`}
                                                    >
                                                        <Pencil size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => onDelete(r.id)}
                                                        className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
                                                        data-testid={`solvencia-del-${r.id}`}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- DocumentosTab ----
function DocumentosTab({ documents, onDelete }) {
    if (documents.length === 0) {
        return (
            <div className="bg-white border border-ink-100 rounded-2xl p-12 text-center shadow-pop">
                <FileText className="mx-auto text-ink-300 mb-3" size={36} />
                <div className="font-display text-xl font-black mb-1">Sin documentos</div>
                <div className="text-sm text-ink-500 font-medium">
                    Sube tus facturas, cuentas anuales, pólizas y certificados arriba.
                </div>
            </div>
        );
    }
    return (
        <div className="bg-white border border-ink-100 rounded-2xl shadow-pop overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-100">
                    <tr className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                        <th className="text-left px-4 py-3">Documento</th>
                        <th className="text-left px-4 py-3 w-32">Tipo</th>
                        <th className="text-left px-4 py-3 w-32">Subido</th>
                        <th className="text-left px-4 py-3 w-28">IA</th>
                        <th className="px-4 py-3 w-20"></th>
                    </tr>
                </thead>
                <tbody>
                    {documents.map((d) => {
                        const meta = DOC_KINDS.find((k) => k.key === d.kind);
                        return (
                            <tr
                                key={d.id}
                                className="border-b border-ink-50 hover:bg-ink-50/50"
                                data-testid={`solvencia-doc-${d.id}`}
                            >
                                <td className="px-4 py-3">
                                    <div className="font-bold text-ink-950 truncate max-w-md">
                                        {d.filename}
                                    </div>
                                    <div className="text-[10px] font-mono text-ink-400 mt-0.5">
                                        {(d.size_bytes / 1024).toFixed(1)} KB
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span
                                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${meta?.accent || "bg-ink-50 text-ink-700 border-ink-200"}`}
                                    >
                                        {kindLabel(d.kind)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 font-mono text-xs text-ink-500">
                                    {d.uploaded_at?.slice(0, 16).replace("T", " ")}
                                </td>
                                <td className="px-4 py-3">
                                    {d.ai_status === "done" ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                                            <CheckCircle2 size={10} />
                                            Extraído
                                        </span>
                                    ) : d.ai_status === "error" ? (
                                        <span
                                            className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full"
                                            title={d.ai_error || ""}
                                        >
                                            <AlertCircle size={10} />
                                            Error
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                                            <Loader2 size={10} className="animate-spin" />
                                            Procesando
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => onDelete(d.id)}
                                        className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
                                        title="Eliminar"
                                        data-testid={`solvencia-doc-del-${d.id}`}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ---- RecordEditor modal (create / edit) ----
function RecordEditor({ record, initialType, onClose, onSaved }) {
    const isEdit = Boolean(record);
    const type = isEdit ? record.type : initialType;
    const subtype = isEdit ? record.subtype || "" : type === "economica" ? "volumen" : "";
    const [year, setYear] = useState(record?.year ?? new Date().getFullYear());
    const [data, setData] = useState({ ...(record?.data || {}) });
    const [saving, setSaving] = useState(false);
    const [activeSubtype, setActiveSubtype] = useState(subtype);

    const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                type,
                subtype: activeSubtype,
                year: year ? Number(year) : null,
                data,
            };
            if (isEdit) {
                await updateSolvencyRecord(record.id, body);
                toast.success("Registro actualizado");
            } else {
                await createSolvencyRecord(body);
                toast.success("Registro creado");
            }
            onSaved();
        } catch {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
                data-testid="solvencia-editor"
            >
                <div className="flex items-center justify-between p-5 border-b border-ink-100 sticky top-0 bg-white">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                            {isEdit ? "Editar registro" : "Nuevo registro"}
                        </div>
                        <div className="font-display text-xl font-black">
                            {type === "tecnica" ? "Solvencia técnica" : "Solvencia económica"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-ink-50"
                        data-testid="solvencia-editor-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    {type === "economica" && !isEdit && (
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                Tipo de registro
                            </Label>
                            <div className="flex gap-2">
                                {[
                                    ["volumen", "Volumen anual"],
                                    ["seguro", "Seguro RC"],
                                ].map(([k, l]) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setActiveSubtype(k)}
                                        className={`px-3 h-8 text-xs font-bold rounded-full transition-all ${activeSubtype === k ? "bg-ink-950 text-white" : "bg-ink-50 text-ink-500 hover:bg-ink-100"}`}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {type === "tecnica" && (
                        <>
                            <FieldRow>
                                <Field label="Cliente">
                                    <Input
                                        value={data.cliente || ""}
                                        onChange={(e) => setField("cliente", e.target.value)}
                                        placeholder="Ej. Ayuntamiento de Sevilla"
                                    />
                                </Field>
                                <Field label="CIF cliente">
                                    <Input
                                        value={data.cliente_cif || ""}
                                        onChange={(e) => setField("cliente_cif", e.target.value)}
                                        placeholder="A12345678"
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Tipo cliente">
                                    <select
                                        value={data.cliente_tipo || "privado"}
                                        onChange={(e) => setField("cliente_tipo", e.target.value)}
                                        className="w-full rounded-md border border-ink-200 h-10 px-3 text-sm bg-white"
                                    >
                                        <option value="publico">Pública</option>
                                        <option value="privado">Privada</option>
                                    </select>
                                </Field>
                                <Field label="Naturaleza">
                                    <select
                                        value={data.naturaleza || "servicio"}
                                        onChange={(e) => setField("naturaleza", e.target.value)}
                                        className="w-full rounded-md border border-ink-200 h-10 px-3 text-sm bg-white"
                                    >
                                        <option value="servicio">Servicio</option>
                                        <option value="obra">Obra</option>
                                        <option value="suministro">Suministro</option>
                                    </select>
                                </Field>
                            </FieldRow>
                            <Field label="Objeto del contrato">
                                <Input
                                    value={data.objeto || ""}
                                    onChange={(e) => setField("objeto", e.target.value)}
                                    placeholder="Descripción del servicio prestado"
                                />
                            </Field>
                            <FieldRow>
                                <Field label="Fecha (YYYY-MM-DD)">
                                    <Input
                                        type="date"
                                        value={data.fecha || ""}
                                        onChange={(e) => {
                                            setField("fecha", e.target.value);
                                            if (e.target.value) setYear(Number(e.target.value.slice(0, 4)));
                                        }}
                                    />
                                </Field>
                                <Field label="Importe sin IVA (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.importe ?? ""}
                                        onChange={(e) => setField("importe", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                            </FieldRow>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(data.certificado)}
                                    onChange={(e) => setField("certificado", e.target.checked)}
                                    className="w-4 h-4 accent-brand-600"
                                />
                                <span className="text-sm font-bold text-ink-700">
                                    Dispongo de certificado de buena ejecución
                                </span>
                            </label>
                        </>
                    )}

                    {type === "economica" && activeSubtype === "volumen" && (
                        <>
                            <FieldRow>
                                <Field label="Ejercicio">
                                    <Input
                                        type="number"
                                        value={year || ""}
                                        onChange={(e) => setYear(e.target.value)}
                                    />
                                </Field>
                                <Field label="Cifra de negocios (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.cifra_negocios ?? ""}
                                        onChange={(e) => setField("cifra_negocios", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Activo corriente (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.activo_corriente ?? ""}
                                        onChange={(e) => setField("activo_corriente", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                                <Field label="Pasivo corriente (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.pasivo_corriente ?? ""}
                                        onChange={(e) => setField("pasivo_corriente", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Patrimonio neto (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.patrimonio_neto ?? ""}
                                        onChange={(e) => setField("patrimonio_neto", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                                <Field label="Resultado ejercicio (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.resultado_ejercicio ?? ""}
                                        onChange={(e) => setField("resultado_ejercicio", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                            </FieldRow>
                        </>
                    )}

                    {type === "economica" && activeSubtype === "seguro" && (
                        <>
                            <FieldRow>
                                <Field label="Aseguradora">
                                    <Input
                                        value={data.aseguradora || ""}
                                        onChange={(e) => setField("aseguradora", e.target.value)}
                                        placeholder="Ej. MAPFRE"
                                    />
                                </Field>
                                <Field label="Nº Póliza">
                                    <Input
                                        value={data.numero_poliza || ""}
                                        onChange={(e) => setField("numero_poliza", e.target.value)}
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Tipo">
                                    <select
                                        value={data.tipo || "RC General"}
                                        onChange={(e) => setField("tipo", e.target.value)}
                                        className="w-full rounded-md border border-ink-200 h-10 px-3 text-sm bg-white"
                                    >
                                        <option>RC General</option>
                                        <option>RC Profesional</option>
                                        <option>Decenal</option>
                                        <option>Otra</option>
                                    </select>
                                </Field>
                                <Field label="Suma asegurada (€)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={data.suma_asegurada ?? ""}
                                        onChange={(e) => setField("suma_asegurada", e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Vigencia inicio">
                                    <Input
                                        type="date"
                                        value={data.vigencia_inicio || ""}
                                        onChange={(e) => setField("vigencia_inicio", e.target.value)}
                                    />
                                </Field>
                                <Field label="Vigencia fin">
                                    <Input
                                        type="date"
                                        value={data.vigencia_fin || ""}
                                        onChange={(e) => setField("vigencia_fin", e.target.value)}
                                    />
                                </Field>
                            </FieldRow>
                            <Field label="Objeto / actividad asegurada">
                                <Input
                                    value={data.objeto || ""}
                                    onChange={(e) => setField("objeto", e.target.value)}
                                />
                            </Field>
                        </>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-ink-100 sticky bottom-0 bg-white">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="rounded-full h-10 px-5 border-ink-200 font-bold"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-10 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold shadow-pop"
                        data-testid="solvencia-editor-save"
                    >
                        {saving ? (
                            <Loader2 size={14} className="animate-spin mr-1.5" />
                        ) : null}
                        {isEdit ? "Guardar cambios" : "Crear registro"}
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function FieldRow({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                {label}
            </Label>
            {children}
        </div>
    );
}
